import { useMutation } from '@apollo/client';
import type { DeleteTaxonomyOrgMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { DeleteTaxonomyOrgDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import { useDeleteTaxonomyOrgTRPC } from './useDeleteTaxonomyOrgTRPC';

/**
 * Consumer input keeps the legacy `{ TaxonomyId, OrgKey }` shape. `OrgKey` is
 * only forwarded to the Apollo branch — the tRPC path derives the org from the
 * authenticated caller server-side and ignores any client-sent org.
 */
export interface DeleteTaxonomyOrgInput {
  TaxonomyId: string;
  OrgKey: string;
}

/**
 * Flag-gated wrapper for the compound `deleteTaxonomyOrg` mutation (RSP-6488):
 * deletes the org's taxonomy_org row then the parent taxonomy iff orphaned.
 * Defaults to Apollo, delegates to the tRPC twin when the `trpc` flag is on.
 * Consumers import THIS hook.
 */
export const useDeleteTaxonomyOrg = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [deleteGraphQL, graphqlState] = useMutation(DeleteTaxonomyOrgDocument, {
    update: (cache) => {
      evictField(cache, 'taxonomy');
      evictField(cache, 'taxonomy_org');
      evictField(cache, 'taxonomy_audit');
    },
  });

  const trpcMutation = useDeleteTaxonomyOrgTRPC();

  const deleteTaxonomyOrg = async (
    input: DeleteTaxonomyOrgInput
  ): Promise<DeleteTaxonomyOrgMutation> => {
    if (trpcEnabled) {
      return trpcMutation.deleteTaxonomyOrg({ TaxonomyId: input.TaxonomyId });
    }

    const result = await deleteGraphQL({
      variables: {
        TaxonomyId: input.TaxonomyId,
        OrgKey: input.OrgKey,
      },
    });
    if (!result.data) {
      throw new Error('Failed to delete taxonomy');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      deleteTaxonomyOrg,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    deleteTaxonomyOrg,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
